import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { usePagination, DOTS } from '../../../hooks/usePagination';
import Head from 'next/head';
import { IRootState } from '../../../store';
import { UTILS } from '../../../constants/utils';
import eventClickProductKnowledgeList from '../../../helpers/emarsys/product-knowledge/eventClickProductKnowledgeList';
import eventClickNewsList from '../../../helpers/emarsys/news/eventClickNewsList';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';
import { FEATURE } from '../../../constants/emarsys/feature';
import eventClickListingPromo from '../../../helpers/emarsys/promo/eventClickListingPromo';
import { PAGE_NAME_PROMO } from '../../../constants/emarsys/promo';
const PUBLIC_URL = process.env.NEXT_PUBLIC_BASEPATH_URL;

interface PaginationProps {
  totalCount: number;
  totalPages: number;
  paginationBaseUrl?: string;
  pageSize: number;
  feature?: string;
  breadcrumbItems?: IBreadcrumbs[];
  emarsysData?: {
    pageTitle: string;
    category: string;
    page_number: number;
  };
  disableNext?: boolean;
}
const Pagination = ({
  totalCount,
  totalPages,
  paginationBaseUrl = PUBLIC_URL,
  pageSize,
  feature = '',
  breadcrumbItems = [],
  emarsysData = {
    pageTitle: '',
    category: '',
    page_number: 0,
  },
  disableNext = false,
}: PaginationProps) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const { PAGINATION_SIBLING_COUNT_MOBILE, PAGINATION_SIBLING_COUNT_DESKTOP } = UTILS;

  const { isMobileScreen } = useSelector((state: IRootState) => state.page);

  const { userData } = useSelector((state: IRootState) => state.auth) || {
    userData: null,
  };

  const page = router?.query?.page || 1;

  const currentPage = parseInt(page as string);

  const getSearchParamStringWithNewPageNumber = (pageNumber) => {
    const searchParamString = new URL(paginationBaseUrl + router.asPath).search;
    const searchParams = new URLSearchParams(searchParamString);
    searchParams.set('page', pageNumber);
    return searchParams.toString();
  };

  const getSearchParamStringWithoutPage = () => {
    const searchParamString = new URL(paginationBaseUrl + router.asPath).search;
    const searchParams = new URLSearchParams(searchParamString);
    searchParams.delete('page');
    const resultSearchParam = searchParams.toString();
    if (!resultSearchParam) return '';
    return `?${resultSearchParam}`;
  };

  const siblingCount = isMobileScreen
    ? PAGINATION_SIBLING_COUNT_MOBILE
    : PAGINATION_SIBLING_COUNT_DESKTOP;

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  const lastPage = Math.min(Math.max(currentPage + 2, 5), totalPages);
  const firstPage = Math.max(1, lastPage - 4);

  const prevPageLink = useMemo(() => {
    if (currentPage - 1 === 1) {
      return getSearchParamStringWithoutPage();
    }
    return `?${getSearchParamStringWithNewPageNumber(currentPage - 1)}`;
  }, [router?.query, currentPage]);

  const nextPageLink = useMemo(() => {
    return `?${getSearchParamStringWithNewPageNumber(currentPage + 1)}`;
  }, [router?.query, currentPage]);

  return (
    <>
      <Head>
        {currentPage > 1 && <link rel="prev" href={`${paginationBaseUrl}${prevPageLink}`} />}

        {currentPage < totalPages && (
          <link rel="next" href={`${paginationBaseUrl}${nextPageLink}`} />
        )}
      </Head>
      <nav className="container w-full">
        <ul className="flex items-center justify-center gap-4 lg:justify-end lg:gap-3">
          <li>
            <Link
              href={`${paginationBaseUrl}${prevPageLink}`}
              className={`flex items-center p-2 text-sm text-reliableBlack border border-reliableBlack10 rounded lg:border-transparent lg:p-0 lg:mr-1 ${
                currentPage === 1 &&
                'bg-[#919EAB] text-reliableBlack10 bg-opacity-50 lg:bg-transparent lg:text-reliableBlack30 pointer-events-none border-transparent'
              }`}
              onClick={() => {
                if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
                  eventClickProductKnowledgeList(
                    emarsysData?.pageTitle,
                    emarsysData?.category,
                    emarsysData?.page_number - 1,
                    'list review',
                    'sebelumnya'
                  );
                } else if (feature === 'NEWS_LIST') {
                  eventClickNewsList(
                    emarsysData?.pageTitle,
                    emarsysData?.category,
                    emarsysData?.page_number - 1,
                    'list berita',
                    'sebelumnya'
                  );
                } else if (feature === FEATURE.LISTING_PROMO) {
                  eventClickListingPromo({
                    dispatch,
                    customerNo: userData?.customerNo,
                    breadcrumbItems: breadcrumbItems,
                    pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
                    pageTitle: emarsysData?.pageTitle,
                    sectionName: 'list promo',
                    buttonName: 'sebelumnya',
                    newParams: {
                      ...(breadcrumbItems?.[2]
                        ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
                        : {}),
                      page_category: emarsysData?.category?.toLowerCase(),
                      page_number: emarsysData?.page_number - 1,
                    },
                  });
                }
              }}
              role="link"
              aria-disabled={currentPage === 1}
              aria-label="sebelumnya link"
            >
              <MdChevronLeft size="18px" className="currentColor lg:text-[#323232]" />
              <span className="hidden lg:inline-block">Sebelumnya</span>
            </Link>
          </li>
          {lastPage > firstPage ? (
            paginationRange?.map((page, index) => {
              // If the pageItem is a DOT, render the DOTS unicode character
              if (page === DOTS) {
                return (
                  <li key={`${page}-${index}`} className="cursor-default">
                    &#8230;
                  </li>
                );
              }

              // Return page number
              return (
                <li key={`${page}-${index}`}>
                  <Link
                    href={
                      page === 1
                        ? `${paginationBaseUrl}${getSearchParamStringWithoutPage()}`
                        : `${paginationBaseUrl}?${getSearchParamStringWithNewPageNumber(page)}`
                    }
                    onClick={() => {
                      if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
                        eventClickProductKnowledgeList(
                          emarsysData?.pageTitle,
                          emarsysData?.category,
                          page,
                          'list review',
                          'pagination'
                        );
                      } else if (feature === 'NEWS_LIST') {
                        eventClickNewsList(
                          emarsysData?.pageTitle,
                          emarsysData?.category,
                          page,
                          'list berita',
                          'pagination'
                        );
                      } else if (feature === FEATURE.LISTING_PROMO) {
                        eventClickListingPromo({
                          dispatch,
                          customerNo: userData?.customerNo,
                          breadcrumbItems: breadcrumbItems,
                          pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
                          pageTitle: emarsysData?.pageTitle,
                          sectionName: 'list promo',
                          buttonName: 'pagination',
                          newParams: {
                            ...(breadcrumbItems?.[2]
                              ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
                              : {}),
                            page_category: emarsysData?.category?.toLowerCase(),
                            page_number: page,
                          },
                        });
                      }
                    }}
                    className={`py-2 px-4 text-sm font-normal text-reliableBlack border border-[#E9E9E9] rounded lg:border-transparent ${
                      currentPage === page && 'text-supportiveRed !border-supportiveRed'
                    }`}
                    role="link"
                  >
                    {page}
                  </Link>
                </li>
              );
            })
          ) : (
            <span
              className={`py-2 px-4 text-sm font-normal text-reliableBlack border border-[#E9E9E9] rounded lg:border-transparent text-supportiveRed !border-supportiveRed
        `}
            >
              1
            </span>
          )}
          <li>
            <Link
              href={`${paginationBaseUrl}${nextPageLink}`}
              className={`flex items-center p-2 text-sm text-reliableBlack border border-[#E9E9E9] rounded lg:border-transparent lg:p-0 lg:ml-1 ${
                (currentPage === totalPages || disableNext) &&
                'bg-[#919EAB] text-reliableBlack10 bg-opacity-50 lg:bg-transparent lg:text-reliableBlack30 pointer-events-none border-transparent'
              }`}
              onClick={() => {
                if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
                  eventClickProductKnowledgeList(
                    emarsysData?.pageTitle,
                    emarsysData?.category,
                    currentPage + 1,
                    'list review',
                    'berikutnya'
                  );
                } else if (feature === 'NEWS_LIST') {
                  eventClickNewsList(
                    emarsysData?.pageTitle,
                    emarsysData?.category,
                    currentPage + 1,
                    'list berita',
                    'berikutnya'
                  );
                } else if (feature === FEATURE.LISTING_PROMO) {
                  eventClickListingPromo({
                    dispatch,
                    customerNo: userData?.customerNo,
                    breadcrumbItems: breadcrumbItems,
                    pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
                    pageTitle: emarsysData?.pageTitle,
                    sectionName: 'list promo',
                    buttonName: 'berikutnya',
                    newParams: {
                      ...(breadcrumbItems?.[2]
                        ? { menu_cat_lvl2: breadcrumbItems?.[2].name?.toLowerCase() }
                        : {}),
                      page_category: emarsysData?.category?.toLowerCase(),
                      page_number: currentPage + 1,
                    },
                  });
                }
              }}
              role="link"
              aria-disabled={currentPage === totalPages}
              aria-label="berikutnya link"
            >
              <span className="hidden lg:inline-block">Berikutnya</span>
              <MdChevronRight size="18px" className="currentColor lg:text-[#323232]" />
            </Link>
          </li>
        </ul>
      </nav>
    </>
  );
};

Pagination.defaulProps = {
  totalPages: 5,
};

export default Pagination;
